import { errorHandler } from "src/app/util/errorHandler";
import { environment } from "../../../../environments/environment";
import { Router } from "@angular/router";
import { Component, OnInit } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { authData, saveAuth } from "src/app/util/auth";
import { DataService } from "src/app/http/data.service";

@Component({
  selector: "app-course-registration-form",
  templateUrl: "./course-registration-form.component.html",
})
export class CourseRegistrationFormComponent implements OnInit {
  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private dataService: DataService
  ) {}

  courses: any = [];
  filter: string = "";
  payload: any = [];
  sessions: any = [];
  semesters: any = [];
  session: string = "";
  semester: string = "";
  sessionAndSemesterSelected: boolean = false;

  _auth = authData();

  savePayload: any = [];

  loading = false;
  skeletonLoader = false;

  _check: any;
  sessionAndSemester: any = {};

  show = false;
  notification: any = { message: "notification?.message", error: true };

  httpOptions = {
    headers: new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: "Bearer " + this._auth.Token,
    }),
  };

  ngOnInit(): void {}

  fetchSessionAndSemester(): Promise<any> {
    this.loading = true;
    return this.httpClient
      .get(`${environment.BASE_URL}/courses/getSessionsAndSemesters`, this.httpOptions)
      .toPromise()
      .then((res: any) => {
        this.loading = false;
        if (res.Success) {
          this.sessionAndSemester = res.Data;
          this.sessions = res.Data.Sessions;
          this.semesters = res.Data.Semesters;
        } else {
          this.notification = {
            message: res.Message || "An error occurred.",
            error: true,
          };
        }
      })
      .catch((error) => {
        this.loading = false;
        this.notification = errorHandler(error);
        
      });
  }

  proceedToCourseSelection(): void {
    if (!this.session || !this.semester) {
      this.notification = {
        message: "Please select both session and semester",
        error: true
      };
      return;
    }
    
    this.sessionAndSemesterSelected = true;
    this.performChecks();
  }

  backToSessionSelection(): void {
    this.sessionAndSemesterSelected = false;
    this.courses = [];
    this.payload = [];
    this.savePayload = [];
    this.notification = { message: "", error: false };
  }

  performChecks(): void {
    let message = "";
    this.loading = true;
    
    this.dataService
      .fetchChecks()
      .then((result) => {
        if (result.Success) {
          const {
            LateRegAppLine,
            PassportUploaded,
            AllowRegistration,
            RegistrationClosed,
            RegistrationLocked,
            OutstandingFeesName,
            StudentHasFilledBioData,
            StudentHasFilledMedicalForm,
            RequireStudentToFillMedicalForm,
            OutstandingFeesForRegistration,
            PaidLateRegistrationCollection,
            PassportPhotoRequiredForRegistration,
            DisablePreSelectedCompulsoryCourses,
          } = (this._check = result.Data);

          if (!AllowRegistration)
            message = "Sorry, registration is not allowed at the moment";
          else if (PassportPhotoRequiredForRegistration && !PassportUploaded)
            message = "Please upload your passport photograph";
          else if (RegistrationLocked)
            message =
              "Your registration has been locked for this session and semester";
          else if (RegistrationClosed) message = "Registration is closed";
          else if (LateRegAppLine && !PaidLateRegistrationCollection)
            message = "You need to pay the late registration fee";
          else if (OutstandingFeesForRegistration)
            message = "Please pay your: " + OutstandingFeesName;
          else if (!StudentHasFilledBioData)
            message = "You haven't filled your bio-data form";
          else if (
            RequireStudentToFillMedicalForm &&
            !StudentHasFilledMedicalForm
          )
            message = "You haven't filled your medical form";

          // Network/alt error handler
          if (!Object.keys(this._check).length) message = "An error occurred";

          this.notification = {
            message,
            error: !!message,
          };

          if (!message) this.fetchCourse();
          else this.loading = false;
        }
      })
      .catch((error) => {
        this.loading = false;
        if (!Object.keys(this._check).length) message = "An error occurred";
      });
  }

  fetchCourse() {
    if (!this.session || !this.semester) return;
    this.skeletonLoader = true;
    this.loading = false;

    const cbaseUrl = `${environment.BASE_URL}/courses/currentCourses?session=${this.session}&semester=${this.semester}`;
    this.httpClient.get(cbaseUrl, this.httpOptions).subscribe({
      next: (res: any) => {
        const currentCoursesData = res.Data;

        const baseUrl = `${environment.BASE_URL}/courses/allCourses?session=${this.session}&semester=${this.semester}`;
        this.httpClient.get(baseUrl, this.httpOptions).subscribe(
          (res: any) => {
            if (res.Success) {
              this.skeletonLoader = false;
              this.courses = res.Data;

              this.courses.sort((a: any, b: any) =>
                a.Compulsory > b.Compulsory ? -1 : 1
              );

              const compulsoryCourses = this.courses.filter(
                (course: any) => course.Compulsory
              );

              // Already-registered courses are always included
              const courses = [...(currentCoursesData?.Courses || [])];

              // Add compulsory courses only when pre-selection is enabled
              if (!this._check.DisablePreSelectedCompulsoryCourses) {
                courses.push(...compulsoryCourses);
              }

              this.savePayload = Array.from(
                new Map(
                  courses.map((course: any) => [
                    course.CourseCode,
                    {
                      CourseCode: course.CourseCode,
                      Compulsory: course.Compulsory,
                    },
                  ])
                ).values()
              );

              this.payload = this.savePayload.map((course: any) => course.CourseCode);
            } else {
              this.skeletonLoader = false;
              this.notification = {
                error: true,
                message: res.Message || "An error occurred.",
              };
            }
          },
          (err) => {
            this.skeletonLoader = false;
            this.notification = errorHandler(err);
          }
        );
      },
      error: (err) => {
        this.skeletonLoader = false;
        this.notification = errorHandler(err);
      },
    });
  }

  toggleForm() {
    if (this.loading) return;

    this.filter = "";
    this.show = !this.show;
    this.notification = { message: "", error: false };
    this.sessionAndSemesterSelected = false;
    
    if (this.show) {
      this.performChecks();
      // When opening the modal, fetch sessions and semesters
      this.fetchSessionAndSemester();
    } else {
      // When closing, reset all selections
      this.session = "";
      this.semester = "";
      this.courses = [];
      this.payload = [];
      this.savePayload = [];
    }
  }

  clickCourse(courseCode: string, compulsory: boolean, siwes: boolean) {
    if (siwes) {
      if (!this.payload.includes(courseCode)) {
        // Remove all non-SIWES courses before adding the new SIWES course when checked
        this.payload = this.payload.filter((item: any) =>
          this.isSiwesCourse(item)
        );
        this.savePayload = this.savePayload.filter((item: any) =>
          this.isSiwesCourse(item.CourseCode)
        );

        this.payload.push(courseCode);
        this.savePayload.push({
          CourseCode: courseCode,
          Compulsory: compulsory,
        });
      } else {
        // If the SIWES course is unchecked, remove it
        this.payload = this.payload.filter((item: any) => item !== courseCode);
        this.savePayload = this.savePayload.filter(
          (item: any) => item.CourseCode !== courseCode
        );

        // If no SIWES courses remain, allow all courses again
        if (!this.payload.some((item: any) => this.isSiwesCourse(item))) {
          this.payload = [];
          this.savePayload = [];
        }
      }
    } else {
      // If the course is not SIWES, add or remove it when checked or unchecked
      if (this.payload.includes(courseCode)) {
        this.payload = this.payload.filter((item: any) => item !== courseCode);
        this.savePayload = this.savePayload.filter(
          (item: any) => item.CourseCode !== courseCode
        );
      } else {
        this.payload.push(courseCode);
        this.savePayload.push({
          CourseCode: courseCode,
          Compulsory: compulsory,
        });
      }
    }
  }

  filterCourses(): any {
    return this.courses.filter(
      (course: any) =>
        course.CourseCode.toLowerCase().includes(this.filter.toLowerCase()) ||
        course.CourseTitle.toLowerCase().includes(this.filter.toLowerCase())
    );
  }

  isSiwesCourse(courseCode: string): boolean {
    const course = this.courses.find(
      (course: any) => course.CourseCode === courseCode
    );
    return course ? course.SIWES === true : false;
  }
  
  isSiwesSelected(): boolean {
    return this.payload.some((code: string) => {
      const course = this.courses.find((c: any) => c.CourseCode === code);
      return course ? course.SIWES === true : false;
    });
  }

  saveCourses() {
    if (!this.payload.length) return;

    this.loading = true;
    this.notification = {};
    const baseUrl = `${environment.BASE_URL}/courses/saveCourses`;

    const requestData = {
      Courses: this.savePayload,
      Session: this.session,
      Semester: this.semester
    };
    
    this.httpClient
      .post(baseUrl, requestData, this.httpOptions)
      .subscribe({
        next: (res: any) => {
          if (res.Success) {
            this.loading = false;
            const { NewMatricNo, NewToken } = res.Data;
            if (NewMatricNo) {
              saveAuth(
                {
                  ...this._auth,
                  NewMatricNumber: NewMatricNo,
                  Token: NewToken,
                },
                "_auth"
              );
            }
           location.href = "/courses?#Courses+registered+successfully";
          } else {
            this.loading = false;
            this.notification = {
              error: true,
              message: res.Message || "An error occurred.",
            };
          }
        },
        error: (err) => {
          this.loading = false;
          this.notification = errorHandler(err);
        },
      });
  }
}